import { useCallback } from 'react';
import { Box } from '@interstate/components/Box';
import { CheckBox } from '@interstate/components/CheckBox';
import { Typography } from '@interstate/components/Typography';
import { Accordion, AccordionDetails, AccordionSummary } from '@interstate/components/AccordionGroup';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import type { InterstateOnChangeCallback } from '@interstate/components/InterstateEvents';
import type { CheckBoxEventValue } from '@interstate/components/CheckBox';
import type { PrintableScopeSummary } from '@makemydeal/dr-dash-store/selectors/printPdfDealSelectors';
import { PrintGridControls } from './PrintGridControls';
import type { ScopeGridInfo } from './usePrintGridSelection';

export interface PrintScenarioSelectorProps {
    scopeSummaries: PrintableScopeSummary[];
    selectedScopeIds: string[];
    onSelectionChange: (selectedScopeIds: string[]) => void;
    isFourSquare?: boolean;
    scopeGridInfos?: ScopeGridInfo[];
    onToggleTermIndex?: (scopeId: string, index: number) => void;
    onToggleDownPaymentIndex?: (scopeId: string, index: number) => void;
}

const renderScenarioDetails = (summary: PrintableScopeSummary) => (
    <Box sx={{ paddingLeft: 3.5 }}>
        <Typography variant="body-sm" color="sem.color.on-surface.muted">
            {summary.vin}
            {summary.vin && summary.stockNumber && ' '}
            {summary.stockNumber && `SIN ${summary.stockNumber}`}
        </Typography>
        <Typography variant="body-sm">{summary.paymentSummary}</Typography>
    </Box>
);

export const PrintScenarioSelector = ({
    scopeSummaries,
    selectedScopeIds,
    onSelectionChange,
    isFourSquare = false,
    scopeGridInfos,
    onToggleTermIndex,
    onToggleDownPaymentIndex
}: PrintScenarioSelectorProps) => {
    const theme = useInterstateTheme();
    const accordionBg = theme.tokens.BaseColorGray50;

    const handleToggle = useCallback(
        (scopeId: string): InterstateOnChangeCallback<CheckBoxEventValue> =>
            (event) => {
                const checked = Boolean(event.target.value?.checked);
                if (checked) {
                    onSelectionChange([...selectedScopeIds, scopeId]);
                } else {
                    onSelectionChange(selectedScopeIds.filter((id) => id !== scopeId));
                }
            },
        [selectedScopeIds, onSelectionChange]
    );

    /* istanbul ignore next -- defensive optional chain when scopeGridInfos not provided */
    const getGridInfoForScope = (scopeId: string): ScopeGridInfo | undefined =>
        scopeGridInfos?.find((info) => info.scopeId === scopeId);

    if (isFourSquare) {
        return (
            <Box data-testid="print-scenario-selector">
                <Typography variant="h5" tag="h3" sx={{ marginBottom: 1 }}>
                    Print Scenarios
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {scopeSummaries.map((summary) => (
                        <Box
                            key={summary.scopeId}
                            sx={{ flex: '1 1 0', minWidth: 200 }}
                            data-testid={`scenario-card-${summary.scopeId}`}
                        >
                            <CheckBox
                                id={`scenario-${summary.scopeId}`}
                                checked={selectedScopeIds.includes(summary.scopeId)}
                                label={summary.vehicleName}
                                onChange={handleToggle(summary.scopeId)}
                                data-testid={`scenario-checkbox-${summary.scopeId}`}
                                value=""
                            />
                            {renderScenarioDetails(summary)}
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }

    return (
        <Box data-testid="print-scenario-selector">
            <Accordion
                defaultExpanded={false}
                data-testid="print-scenarios-accordion"
                id="print-scenarios-accordion"
                sx={{
                    backgroundColor: theme.tokens.BaseColorWhite,
                    '&.Mui-expanded': { backgroundColor: accordionBg }
                }}
            >
                <AccordionSummary chevronPosition="left" data-testid="print-scenarios-accordion-summary">
                    <Typography variant="h5" tag="h3" color="sem.color.on-surface.interactive.rest">
                        Print Scenarios
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                            {scopeSummaries.map((summary) => {
                                const isSelected = selectedScopeIds.includes(summary.scopeId);
                                const gridInfo = getGridInfoForScope(summary.scopeId);

                                return (
                                    <Box
                                        key={summary.scopeId}
                                        data-testid={`scenario-card-${summary.scopeId}`}
                                        sx={{
                                            flex: '1 1 0',
                                            minWidth: 350,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            '& > .ids-checkbox-root': { backgroundColor: 'transparent' }
                                        }}
                                    >
                                        <CheckBox
                                            id={`scenario-${summary.scopeId}`}
                                            checked={isSelected}
                                            label={summary.vehicleName}
                                            onChange={handleToggle(summary.scopeId)}
                                            data-testid={`scenario-checkbox-${summary.scopeId}`}
                                            value=""
                                        />
                                        {renderScenarioDetails(summary)}
                                        {/* istanbul ignore next -- guard for optional grid props */}
                                        {gridInfo?.hasGrid && gridInfo.gridData && onToggleTermIndex && onToggleDownPaymentIndex ? (
                                            <PrintGridControls
                                                scopeId={summary.scopeId}
                                                gridData={gridInfo.gridData}
                                                selection={gridInfo.selection}
                                                disabled={!isSelected}
                                                onToggleTerm={onToggleTermIndex}
                                                onToggleDownPayment={onToggleDownPaymentIndex}
                                            />
                                        ) : (
                                            <Typography
                                                variant="body-sm"
                                                color="sem.color.on-surface.muted"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    flex: '1 0 auto',
                                                    ml: 3.5,
                                                    minHeight: '120px'
                                                }}
                                                data-testid={`grid-disabled-message-${summary.scopeId}`}
                                            >
                                                Payment grid not enabled for this scenario.
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};
